import * as React from 'react'
import { useState, useCallback } from 'react'
import { styled } from 'styled-components'
import { Row, Col } from 'react-bootstrap'
import * as domain from '@packages/domain'
import { b, constVoid, identity, O, pipe, RA } from '@packages/prelude'
import { react } from '../commons'
import { Option } from './Option'
import { OptionDetail } from './OptionDetail'

const GridContainer = styled.div`
  margin-top: 1rem;
`

export interface OptionsGridProps {
  step: domain.Step.Type
  isOptionSelected: (optionId: domain.OptionId.Type) => boolean
  onOptionSelect: (option: domain.Option.Option) => void
  onOptionNoneSelect: (option: domain.Option.OptionNone) => void
}

export const OptionsGrid: React.FC<OptionsGridProps> = ({ step, isOptionSelected, onOptionSelect, onOptionNoneSelect }) => {
  const [detailOption, setDetailOption] = useState<O.Option<domain.Option.Type>>(O.none)
  const [showDetail, setShowDetail] = useState(false)

  const handleSelectOption = useCallback(
    (option: domain.Option.Option) => {
      onOptionSelect(option)
    },
    [onOptionSelect]
  )

  const handleSelectOptionNone = useCallback(
    (option: domain.Option.OptionNone) => {
      onOptionNoneSelect(option)
    },
    [onOptionNoneSelect]
  )

  const handleShowDetail = useCallback((option: domain.Option.Type) => {
    setDetailOption(O.some(option))
    setShowDetail(true)
  }, [])

  const handleCloseDetail = useCallback(() => {
    setShowDetail(false)
  }, [])

  const handleRadioOption = useCallback(
    (option: domain.Option.Type) => {
      // For single selection, we need to enforce that one option is always selected after the first selection
      const currentSelected = RA.size(step.options.filter(opt => isOptionSelected(opt.id)))
      const isCurrentlySelected = isOptionSelected(option.id)

      // If this is the only selected option, don't allow deselection
      // Otherwise, allow normal selection behavior
      pipe(
        [isCurrentlySelected, currentSelected === 1],
        RA.foldMap(b.MonoidAll)(identity),
        b.fold(
          () =>
            domain.Option.ADT.match({
              Option: option => handleSelectOption(option),
              OptionNone: option => handleSelectOptionNone(option)
            })(option),
          constVoid
        )
      )
    },
    [step.options, isOptionSelected, handleSelectOption, handleSelectOptionNone]
  )

  return (
    <GridContainer>
      <Row xs={2} md={4} lg={5} xl={6} className="g-3">
        {pipe(
          step.options,
          RA.foldMap(react.monoid)(
            domain.Option.ADT.match({
              Option: option => (
                <Col key={String(option.id)}>
                  <Option
                    option={option}
                    isSelected={isOptionSelected(option.id)}
                    onSelect={() => (step.selectionMode === 'single' ? handleRadioOption(option) : handleSelectOption(option))}
                    onInfoClick={() => handleShowDetail(option)}
                    stepId={step.id}
                    selectionMode={step.selectionMode}
                  />
                </Col>
              ),
              OptionNone: option => (
                <Col key={String(option.id)}>
                  <Option.OptionNone
                    option={option}
                    isSelected={isOptionSelected(option.id)}
                    onSelect={() =>
                      step.selectionMode === 'single' ? handleRadioOption(option) : handleSelectOptionNone(option)
                    }
                    stepId={step.id}
                    selectionMode={step.selectionMode}
                  />
                </Col>
              )
            })
          )
        )}
      </Row>

      {pipe(
        detailOption,
        O.foldMap(react.monoid)(option => <OptionDetail option={option} show={showDetail} onClose={handleCloseDetail} />)
      )}
    </GridContainer>
  )
}
